import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Modal } from 'ksnm-common-ui/lib/Modal';
import { Notification } from 'ksnm-common-ui/lib/Notification';
import type { ReactElement } from 'react';

import { objectFieldsToCamelCase } from '@src/utils/objectFieldsToCamelCase';
import { reportSettingsStore } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';
import type { ReportSettingDtoDefId } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';

import { SelectForm } from './SelectForm';

interface ICopySettingsModal {
  versions: { id: string; name: string | undefined }[];
  visible: boolean;
  toggleVisibility: () => void;
  update: () => void;
}

export const CopySettingsModal = observer(
  ({ versions, toggleVisibility, visible, update }: ICopySettingsModal): ReactElement => {
    const { addReportSettings, fetch, checked } = reportSettingsStore;

    const onCopy = useCallback(
      async (version: string) => {
        const objectToCopy = {
          ...objectFieldsToCamelCase({
            ...checked[0],
            url: (checked[0]?.url as string)?.split(' - ')[0],
            reportVersionCode: version,
          }),
        };

        const data = await addReportSettings(objectToCopy as ReportSettingDtoDefId);

        if (data?.reportVersionCode !== version) {
          Notification({
            message: 'Произошла ошибка при копировании',
            type: 'error',
          });
        } else {
          Notification({
            message: 'Копирование выполнено успешно',
            type: 'success',
          });

          toggleVisibility();
          fetch();
          update();
        }
      },
      [addReportSettings, checked, fetch, toggleVisibility, update],
    );

    const onSubmit = useCallback(
      async (values) => {
        if (!values?.version) return;

        onCopy(values?.version);
      },
      [onCopy],
    );

    return (
      <Modal
        open={visible}
        centered
        footer={null}
        onCancel={toggleVisibility}
        width={500}
        closeIcon={<Icon name="close" noFill />}
        destroyOnClose
      >
        <FormWrapper onSubmit={onSubmit} layout="vertical">
          <SelectForm items={versions} />
        </FormWrapper>
      </Modal>
    );
  },
);
